import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../firebase';
import TopNavigation from '../components/TopNavigation';
import LeftNavSidebar from '../components/LeftNavSidebar';
import RightWidgetsSidebar from '../components/RightWidgetsSidebar';
import ImageWithFallback from '../components/ImageWithFallback';
import { useLocation, useParams } from 'react-router-dom';
import MobileContainer from '../components/MobileContainer';
import Card from '../components/Card';
import { followUser, getUserById, listFollowers, listFollowing, unfollowUser } from '../utils/secureFunctions';

interface UserLite { id: string; name: string; photoURL: string; username?: string; followers?: number; }

type Tab = 'followers' | 'following';

const FollowersFollowing: React.FC = () => {
  const { userId: routeUserId } = useParams();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialTab = (searchParams.get('tab') === 'following' ? 'following' : 'followers') as Tab;
  const [active, setActive] = useState<Tab>(initialTab);
  const [following, setFollowing] = useState<string[]>([]);
  const [followersList, setFollowersList] = useState<UserLite[]>([]);
  const [followingList, setFollowingList] = useState<UserLite[]>([]);
  const [loading, setLoading] = useState(false);
  const [targetUser, setTargetUser] = useState<{ id: string; name?: string; username?: string } | null>(null);

  const targetUid = routeUserId || auth.currentUser?.uid || null;

  useEffect(() => {
    const load = async () => {
      if (!auth.currentUser || !targetUid) return;
      setLoading(true);
      try {
        const [targetRes, followersRes, followingRes, myList] = await Promise.all([
          getUserById(targetUid),
          listFollowers(targetUid, { limit: 100 }),
          listFollowing(targetUid, { limit: 100 }),
          listFollowing(auth.currentUser.uid, { limit: 200 }),
        ]);

        setFollowing((myList?.users || []).map((u) => u.id));

        setTargetUser({ id: targetUid, name: targetRes.profile?.name || 'User', username: targetRes.profile?.username || '' });

        setFollowersList((followersRes.users || []).map((u) => ({
          id: u.id,
          name: u.name || 'User',
          photoURL: u.photoURL || '',
          username: u.username || '',
        })));

        setFollowingList((followingRes.users || []).map((u) => ({
          id: u.id,
          name: u.name || 'User',
          photoURL: u.photoURL || '',
          username: u.username || '',
        })));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [routeUserId, targetUid]);

  const isFollowing = (uid: string) => following.includes(uid);

  const toggleFollow = async (targetId: string) => {
    if (!auth.currentUser || targetId === auth.currentUser.uid) return;
    const currently = isFollowing(targetId);
    try {
      // Optimistic
      setFollowing((prev) => currently ? prev.filter((id) => id !== targetId) : [...prev, targetId]);
      if (currently) {
        await unfollowUser(targetId);
      } else {
        await followUser(targetId);
      }
    } catch (e) {
      // Revert
      setFollowing((prev) => currently ? [...prev, targetId] : prev.filter((id) => id !== targetId));
    }
  };

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const list = active === 'followers' ? followersList : followingList;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--color-background-alt)' }}>
      <TopNavigation />
      {!isMobile && <LeftNavSidebar />}
      {!isMobile && <RightWidgetsSidebar />}
      <div style={{ paddingTop: 0, marginLeft: isMobile ? 0 : 300, marginRight: isMobile ? 0 : 340 }}>
        <MobileContainer>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 8 }}>
          <h2 style={{ margin: 0 }}>Connections{targetUser ? ` • ${targetUser.username ? '@'+targetUser.username : targetUser.name}` : ''}</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setActive('followers')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: active==='followers' ? 'var(--color-primary-50)' : 'transparent' }}>Followers</button>
            <button onClick={() => setActive('following')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: active==='following' ? 'var(--color-primary-50)' : 'transparent' }}>Following</button>
          </div>
        </header>

        {loading ? (
          <div style={{ height: 60 }}></div>
        ) : list.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No users to show.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
            {list.map((u) => (
              <Card key={u.id} padding={10} radius={10}>
                <ImageWithFallback src={u.photoURL} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong>{u.name}</strong>
                  <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>@{u.username || u.id.slice(0,6)} • {u.followers || 0} followers</span>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  {auth.currentUser?.uid !== u.id && (
                    <button onClick={() => toggleFollow(u.id)} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: isFollowing(u.id) ? '#eee' : 'var(--color-primary-600)', color: isFollowing(u.id) ? 'var(--color-text)' : '#fff' }}>
                      {isFollowing(u.id) ? 'Following' : 'Follow'}
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
        </MobileContainer>
      </div>
    </div>
  );
};

export default FollowersFollowing;
